import { Controller } from "@hotwired/stimulus";

const MIN_WIDTH = 260; // narrower than this and the two-column settings rows start wrapping
const PREVIEW_MIN_WIDTH = 320; // the chart preview never gets squeezed below this
const STORAGE_KEY = "report-chart-settings-width";

// Drag-to-resize for the chart settings panel, mirroring the Mo chat sidebar's handle. The panel is a
// fixed-width flex child next to the preview, so the width lives on the element itself rather than a
// CSS custom property, and the drag is clamped so the preview keeps a usable width.
export default class extends Controller {
  static targets = ["handle", "panel"];

  connect() {
    this.restore();
    window.addEventListener("resize", this.reclamp);
  }

  disconnect() {
    window.removeEventListener("resize", this.reclamp);
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  // The panel and preview share one row, so their combined width is fixed: whatever the preview has
  // beyond its minimum is what the panel may take.
  maxWidth() {
    const row = this.element.getBoundingClientRect().width;
    return Math.max(MIN_WIDTH, row - PREVIEW_MIN_WIDTH);
  }

  clamp(px) {
    return Math.max(MIN_WIDTH, Math.min(this.maxWidth(), px));
  }

  setWidth(px) {
    this.width = px;
    this.panelTarget.style.width = `${px}px`;
  }

  restore() {
    const stored = parseInt(localStorage.getItem(STORAGE_KEY), 10);
    if (!Number.isFinite(stored)) return;

    this.preferred = stored;
    this.setWidth(this.clamp(stored));
  }

  // Dragging leftward widens the panel, since it sits to the right of the preview.
  start = (event) => {
    if (event.button !== 0) return;

    event.preventDefault();
    this.handleTarget.setPointerCapture(event.pointerId);
    this.startX = event.clientX;
    this.startWidth = this.panelTarget.getBoundingClientRect().width;
    document.body.classList.add(
      "column-gesture-active",
      "column-gesture-resize",
    );
    this.handleTarget.addEventListener("pointermove", this.move);
    this.handleTarget.addEventListener("pointerup", this.end);
  };

  move = (event) => {
    this.setWidth(this.clamp(this.startWidth - (event.clientX - this.startX)));
  };

  end = (event) => {
    this.handleTarget.releasePointerCapture(event.pointerId);
    this.handleTarget.removeEventListener("pointermove", this.move);
    this.handleTarget.removeEventListener("pointerup", this.end);
    document.body.classList.remove(
      "column-gesture-active",
      "column-gesture-resize",
    );
    this.preferred = this.width;
    localStorage.setItem(STORAGE_KEY, Math.round(this.width));
  };

  // Keep the chosen width valid as the window changes: shrink it when the preview would be squeezed,
  // and grow it back toward the user's preference when room returns.
  reclamp = () => {
    if (!this.preferred || this.frame) return;

    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      const target = this.clamp(this.preferred);
      if (Math.round(target) !== Math.round(this.width)) this.setWidth(target);
    });
  };
}
